/**
 * CrateDiggingCarousel — 3D Coverflow Carousel for Collections & Artists
 * Uses SwiperJS with EffectCoverflow for the "crate-digging" experience.
 *
 * Desktop: Center-focused with perspective:1000px + rotateY on side slides
 * Mobile: Single record per view with high-velocity swipe
 * Flip: Clicking active slide flips to show metadata
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Key, ArrowRight, Star } from 'lucide-react';
import { generateRandomAvatar } from './avatarGenerator';

import 'swiper/css';
import './CrateDiggingCarousel.css';

// ─── Types ──────────────────────────────────────────────────────────

interface Collection {
  id: number;
  tenantId: number;
  name: string | null;
  coverUrl: string | null;
  issueNumber: number;
  productionLimit: number | null;
  productionType: string | null;
  acquiredAt: number;
}

interface Artist {
  id: number;
  artistName: string;
  slug: string;
  profileUrl: string | null;
  joinedAt: number;
  hasUncollectedPressings?: boolean;
  statusText: string | null;
  latestTransmission: { headline: string; date: number | null } | null;
}

function formatEpochDate(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return 'Unknown';
  const ms = value < 1_000_000_000_000 ? value * 1000 : value;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString();
}

// ─── Collection Carousel ────────────────────────────────────────────

interface CollectionCarouselProps {
  collections: Collection[];
  onEnterVault?: (collection: Collection) => void;
}

export function CollectionCarousel({ collections, onEnterVault }: CollectionCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  // Sort by most recent first
  const sorted = [...collections].sort((a, b) => b.acquiredAt - a.acquiredAt);

  const handleSlideClick = useCallback((index: number) => {
    if (index !== activeIndex) {
      swiperRef.current?.slideTo(index);
    }
  }, [activeIndex]);

  useEffect(() => {
    const initialIndex = sorted.length >= 3 ? 1 : 0;
    setActiveIndex(initialIndex);
    swiperRef.current?.slideTo(initialIndex, 0, false);
  }, [sorted.length]);

  return (
    <div className="crate-carousel-wrapper collection-profile-carousel relative">
      <Swiper
        effect="slide"
        grabCursor={false}
        centeredSlides
        observer
        observeParents
        updateOnWindowResize
        slidesPerView="auto"
        slideToClickedSlide={false}
        speed={350}
        allowTouchMove={false}
        simulateTouch={false}
        preventClicks={false}
        preventClicksPropagation={false}
        breakpoints={{
          0: {
            slidesPerView: 1.2,
            spaceBetween: 16,
          },
          768: {
            slidesPerView: 'auto',
            spaceBetween: 0,
          },
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          const initialIndex = sorted.length >= 3 ? 1 : 0;
          swiper.slideTo(initialIndex, 0, false);
          setActiveIndex(initialIndex);
        }}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.activeIndex);
        }}
        className="crate-carousel-swiper"
      >
        {sorted.map((collection, index) => (
          <SwiperSlide
            key={collection.id}
            className="crate-slide"
            onClick={() => handleSlideClick(index)}
          >
            <div className="crate-card-perspective">
              <div className="crate-card crate-card-front">
                {collection.coverUrl ? (
                  <img
                    src={collection.coverUrl}
                    alt={collection.name || 'Vault'}
                    className="w-full h-full object-cover rounded-xl"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] rounded-xl relative overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-[85%] h-[85%] rounded-full border border-white/5 flex items-center justify-center">
                        <div className="w-[70%] h-[70%] rounded-full border border-white/[0.03] flex items-center justify-center">
                          <div className="w-[55%] h-[55%] rounded-full border border-white/[0.03] flex items-center justify-center">
                            <div className="w-[40%] h-[40%] rounded-full border border-white/[0.03] flex items-center justify-center">
                              <div className="w-[70%] h-[70%] rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                                <div className="w-2 h-2 rounded-full bg-white/20" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="absolute inset-x-0 bottom-3 flex justify-center z-10">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEnterVault?.(collection);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-black/65 text-white border border-white/30 font-mono text-[10px] font-bold rounded-md hover:bg-black/80 transition-colors"
                  >
                    View Pressing <ArrowRight className="w-3 h-3" />
                  </button>
                </div>

                <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-black/60 border border-white/20 z-10">
                  <div className="flex items-center gap-1 text-[10px] text-white/80 font-mono">
                    <Key className="w-3 h-3" />
                    <span>
                      #{collection.issueNumber}
                      {collection.productionLimit
                        ? `/${collection.productionLimit}`
                        : ''}
                    </span>
                  </div>
                </div>

                {index === activeIndex && (
                  <div className="absolute top-2 left-2 w-2 h-2 rounded-full bg-white/40 animate-pulse" />
                )}
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

    </div>
  );
}

// ─── Artist Carousel ────────────────────────────────────────────────

interface ArtistCarouselProps {
  artists: Artist[];
  onEnterArtist?: (artist: Artist) => void;
  onActiveArtistChange?: (artist: Artist | null) => void;
}

export function ArtistCarousel({ artists, onEnterArtist, onActiveArtistChange }: ArtistCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  // Sort by most recently joined first
  const sorted = [...artists].sort((a, b) => b.joinedAt - a.joinedAt);

  useEffect(() => {
    onActiveArtistChange?.(sorted[activeIndex] ?? null);
  }, [activeIndex, onActiveArtistChange, sorted]);

  useEffect(() => {
    const initialIndex = sorted.length >= 3 ? 1 : 0;
    setActiveIndex(initialIndex);
    swiperRef.current?.slideTo(initialIndex, 0, false);
  }, [sorted.length]);

  const handleSlideClick = useCallback((index: number) => {
    if (index !== activeIndex) {
      swiperRef.current?.slideTo(index);
    }
  }, [activeIndex]);

  return (
    <div className="crate-carousel-wrapper collection-profile-carousel relative">
      <Swiper
        effect="slide"
        grabCursor={false}
        centeredSlides
        observer
        observeParents
        updateOnWindowResize
        slidesPerView="auto"
        slideToClickedSlide={false}
        speed={350}
        allowTouchMove={false}
        simulateTouch={false}
        preventClicks={false}
        preventClicksPropagation={false}
        breakpoints={{
          0: {
            slidesPerView: 1.2,
            spaceBetween: 16,
          },
          768: {
            slidesPerView: 'auto',
            spaceBetween: 0,
          },
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          const initialIndex = sorted.length >= 3 ? 1 : 0;
          swiper.slideTo(initialIndex, 0, false);
          setActiveIndex(initialIndex);
        }}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.activeIndex);
        }}
        className="crate-carousel-swiper"
      >
        {sorted.map((artist, index) => (
          <SwiperSlide
            key={artist.id}
            className="crate-slide"
            onClick={() => handleSlideClick(index)}
          >
            <div className="crate-card-perspective">
              <div className="crate-card crate-card-front">
                <div className="absolute inset-0">
                  {artist.profileUrl ? (
                    <img src={artist.profileUrl} alt={artist.artistName} className="w-full h-full object-cover" />
                  ) : (
                    <img src={generateRandomAvatar(artist.slug)} alt={artist.artistName} className="w-full h-full object-cover" />
                  )}
                </div>

                <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/90 via-black/55 to-transparent pointer-events-none z-[6]" />
                <div className="absolute inset-x-0 bottom-12 px-3 z-[7] text-center pointer-events-none">
                  <h3 className="text-sm font-bold text-white truncate w-full">
                    {artist.artistName}
                  </h3>
                  <p className="text-[10px] text-white/75 font-mono mt-1">
                    Following since {formatEpochDate(artist.joinedAt)}
                  </p>
                </div>

                {artist.hasUncollectedPressings && (
                  <div className="absolute top-2 right-2 p-1 rounded-full bg-[#121212]/80 border border-amber-300/60 z-10" title="New pressings available">
                    <Star className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />
                  </div>
                )}

                <div className="absolute inset-x-0 bottom-3 flex justify-center z-10">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEnterArtist?.(artist);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-black/65 text-white border border-white/30 font-mono text-[10px] font-bold rounded-md hover:bg-black/80 transition-colors"
                  >
                    View Artist <ArrowRight className="w-3 h-3" />
                  </button>
                </div>

                {index === activeIndex && (
                  <div className="absolute top-2 left-2 w-2 h-2 rounded-full bg-white/40 animate-pulse" />
                )}
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

    </div>
  );
}
